import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const articles = [
  {
    id: "what-is-dzi",
    title: "天珠是什麼？天珠的文化與歷史",
    excerpt: "深入了解西藏天珠的起源、文化意涵與千年傳承，探索這顆神聖之珠背後的故事。",
    tag: "天珠文化",
    tagColor: "#8b6f47",
    tagBg: "#f0e8dc",
    img: "https://readdy.ai/api/search-image?query=Tibetan%20dzi%20bead%20ancient%20agate%20stone%20collection%20on%20aged%20wooden%20surface%20with%20Tibetan%20script%20cultural%20heritage%20photography%20warm%20amber%20light%20documentary%20style%20high%20detail&width=600&height=400&seq=know001&orientation=landscape",
    readTime: "5 分鐘閱讀",
  },
  {
    id: "how-to-choose-crystal",
    title: "如何挑選適合自己的水晶",
    excerpt: "從能量屬性、個人需求到直覺感應，學習如何找到與自己頻率共鳴的水晶夥伴。",
    tag: "水晶指南",
    tagColor: "#7a6a9a",
    tagBg: "#f0edf7",
    img: "https://readdy.ai/api/search-image?query=various%20natural%20crystals%20gemstones%20collection%20amethyst%20quartz%20obsidian%20arranged%20on%20white%20marble%20surface%20lifestyle%20photography%20soft%20natural%20light%20elegant%20minimalist%20style&width=600&height=400&seq=know002&orientation=landscape",
    readTime: "7 分鐘閱讀",
  },
  {
    id: "jade-symbolism",
    title: "翡翠玉石的象徵意義",
    excerpt: "探索翡翠在東方文化中的深厚意涵，了解不同種類玉石所代表的吉祥寓意。",
    tag: "玉石文化",
    tagColor: "#4a7a52",
    tagBg: "#eaf2eb",
    img: "https://readdy.ai/api/search-image?query=jade%20jadeite%20green%20gemstone%20jewelry%20collection%20on%20white%20marble%20with%20green%20leaves%20cultural%20heritage%20photography%20soft%20natural%20light%20elegant%20oriental%20style&width=600&height=400&seq=know003&orientation=landscape",
    readTime: "6 分鐘閱讀",
  },
  {
    id: "thangka-art-value",
    title: "唐卡藝術的文化價值",
    excerpt: "走進唐卡藝術的世界，了解這門古老藝術形式的創作過程、宗教意涵與收藏價值。",
    tag: "藝術文化",
    tagColor: "#c9a96e",
    tagBg: "#faf5ec",
    img: "https://readdy.ai/api/search-image?query=Tibetan%20thangka%20painting%20detail%20Buddhist%20art%20colorful%20mandala%20traditional%20artwork%20close%20up%20cultural%20heritage%20photography%20warm%20golden%20light%20artistic%20style&width=600&height=400&seq=know004&orientation=landscape",
    readTime: "8 分鐘閱讀",
  },
];

export default function Knowledge() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleArticleClick = (id: string) => {
    navigate(`/knowledge/${id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section id="knowledge" className="py-28 md:py-36 bg-[#faf8f4]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#8b6f47]"></span>
            <span
              className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              KNOWLEDGE
            </span>
            <span className="w-10 h-px bg-[#8b6f47]"></span>
          </div>
          <h2
            className="text-[#2d2520] font-bold mb-4 leading-[1.4]"
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            認識能量文化
          </h2>
          <p
            className="text-[#7a6a5a] max-w-xl mx-auto leading-[1.75]"
            style={{
              fontSize: "1.1875rem",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            深入了解東方能量文化的智慧與傳承，探索每一種材質背後的故事與意義。
          </p>
        </div>

        {/* Article Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {articles.map((article, i) => (
            <article
              key={article.id}
              className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-1000 group"
              style={{
                transitionDelay: `${i * 100}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
              }}
              onClick={() => handleArticleClick(article.id)}
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={article.img}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="px-3 py-1 rounded-full text-[0.875rem] font-medium"
                    style={{
                      color: article.tagColor,
                      backgroundColor: article.tagBg,
                      fontFamily: "'Noto Serif TC', serif",
                    }}
                  >
                    {article.tag}
                  </span>
                  <span
                    className="text-[#a89880] text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {article.readTime}
                  </span>
                </div>
                <h3
                  className="text-[#2d2520] font-bold mb-3 leading-[1.5]"
                  style={{
                    fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.title}
                </h3>
                <p
                  className="text-[#7a6a5a] leading-[1.75] mb-5"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 text-[#8b6f47] font-medium text-[1rem]">
                  <span style={{ fontFamily: "'Noto Serif TC', serif" }}>閱讀更多</span>
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All */}
        <div
          className={`text-center mt-12 transition-all duration-1000 delay-400 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <button
            onClick={() => { navigate("/knowledge"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="inline-flex items-center gap-3 px-10 py-4 border-2 border-[#2d2520] text-[#2d2520] rounded-full text-[1rem] font-medium tracking-widest cursor-pointer hover:bg-[#2d2520] hover:text-white transition-all duration-300 whitespace-nowrap"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            瀏覽所有文章
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
